import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Archive, Download, Mail, Trash2, Loader2, FileText, Calendar as CalendarIcon, Sparkles } from 'lucide-react';
import { reportsApi } from '../services/api';
import { EmailReportDialog } from './EmailReportDialog';
import { RegenerateSummaryDialog } from './RegenerateSummaryDialog';
import { useConfirm } from './ConfirmProvider';

export function SavedBundlesDialog({ open, onOpenChange, canDelete = false }) {
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const [bundles, setBundles] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [emailBundleId, setEmailBundleId] = useState(null);
  const [regenBundle, setRegenBundle] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await reportsApi.listBundles();
      setBundles(data);
    } catch {
      toast.error('Failed to load saved bundles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const handleDownload = async (bundle) => {
    setDownloadingId(bundle.id);
    try {
      const blob = await reportsApi.downloadBundle(bundle.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = bundle.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      toast.error('Failed to download bundle');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (bundle) => {
    if (!(await confirm({ title: `Delete saved bundle for ${bundle.client_name}?`, description: 'This cannot be undone.', confirmText: 'Delete bundle', destructive: true }))) return;
    setDeletingId(bundle.id);
    try {
      await reportsApi.deleteBundle(bundle.id);
      toast.success('Bundle deleted');
      setBundles(prev => prev.filter(b => b.id !== bundle.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const formatBytes = (n) => {
    if (!n) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl bg-white max-h-[80vh] overflow-y-auto" data-testid="saved-bundles-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5 text-teal-600" />
              Saved Consolidated Bundles
            </DialogTitle>
            <DialogDescription>
              Every consolidated PDF you&apos;ve saved to the workspace archive. Re-download or email without regenerating.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="py-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-teal-600" />
            </div>
          ) : bundles.length === 0 ? (
            <div className="py-12 text-center">
              <Archive className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">No saved bundles yet. Tick &quot;Save to archive&quot; the next time you consolidate.</p>
            </div>
          ) : (
            <div className="space-y-3 mt-4" data-testid="bundles-list">
              {bundles.map(b => (
                <div key={b.id} className="border border-slate-200 rounded-lg p-4 hover:border-teal-300 transition-colors" data-testid={`bundle-${b.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <FileText className="h-4 w-4 text-teal-600 shrink-0" />
                        <h4 className="font-heading font-semibold text-slate-900 truncate">
                          {b.client_name}
                        </h4>
                        <Badge variant="outline" className="text-xs">
                          {b.report_count} report{b.report_count === 1 ? '' : 's'}
                        </Badge>
                        {b.include_cover && (
                          <Badge className="bg-teal-100 text-teal-700 text-xs">Cover</Badge>
                        )}
                        {b.unified && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">Unified</Badge>
                        )}
                        {b.auto_generated && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">Auto</Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
                        {b.date_range_start && (
                          <span className="inline-flex items-center gap-1">
                            <CalendarIcon className="h-3 w-3" />
                            {b.date_range_start}{b.date_range_end && b.date_range_end !== b.date_range_start ? ` → ${b.date_range_end}` : ''}
                          </span>
                        )}
                        <span>Saved {new Date(b.created_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        {b.created_by_name && <span>by {b.created_by_name}</span>}
                        {b.file_size ? <span>{formatBytes(b.file_size)}</span> : null}
                      </div>
                      {b.note && (
                        <p className="text-xs text-slate-600 mt-2 italic">"{b.note}"</p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(b)}
                        disabled={downloadingId === b.id}
                        data-testid={`download-bundle-${b.id}`}
                      >
                        {downloadingId === b.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                      {b.unified && canDelete && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setRegenBundle(b)}
                          className="border-teal-200 text-teal-700 hover:bg-teal-50"
                          title="Regenerate AI summary"
                          data-testid={`regen-summary-${b.id}`}
                        >
                          <Sparkles className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEmailBundleId(b.id)}
                        className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                        data-testid={`email-bundle-${b.id}`}
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                      {canDelete && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(b)}
                          disabled={deletingId === b.id}
                          className="border-red-200 text-red-700 hover:bg-red-50"
                          data-testid={`delete-bundle-${b.id}`}
                        >
                          {deletingId === b.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <EmailReportDialog
        open={!!emailBundleId}
        onOpenChange={(v) => !v && setEmailBundleId(null)}
        mode="bundle"
        bundleId={emailBundleId}
      />
      {regenBundle && (
        <RegenerateSummaryDialog
          open={!!regenBundle}
          onOpenChange={(v) => !v && setRegenBundle(null)}
          bundle={regenBundle}
          onUpdated={(id, data) => {
            setBundles(prev => prev.map(b => b.id === id ? { ...b, ai_summary: data.summary, ai_summary_tone: data.tone, ai_summary_length: data.length } : b));
          }}
        />
      )}
    </>
  );
}
